import { displayLocaleDate, Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { Institution } from './Institution';
import { InstitutionGroup } from './InstitutionGroup';

export interface AbsencePeriodRow {
  id: number;
  institution_id: number;
  institution_group_id: number;
  start_date: string;
  end_date: string;
  amber_threshold: number;
  red_threshold: number;
  member_count: number;
  tracked_event_count: number;
  booking_count: number;
  absences_count: number;
  attended_count: number;
  not_occurred_event_count: number;
  members_in_amber_count: number;
  members_in_red_count: number;
  last_computed_at: string;
  event_ids: string;
  computation_notes: string;
}

export const AbsencePeriod: Entity<AbsencePeriodRow> = {
  id: 'AbsencePeriod',
  title: 'Absence Period',
  service: 'main',
  rowLabel: (row: AbsencePeriodRow) => `${displayLocaleDate(row.start_date)} to ${displayLocaleDate(row.end_date)}`,
  rowLabelDynamic: (row: AbsencePeriodRow) =>
    `<lookup:InstitutionGroup:${row.institution_group_id}> (${displayLocaleDate(row.start_date)} to ${displayLocaleDate(
      row.end_date
    )})`,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.absence_period',
  },
  security: [
    {
      role: 'admin',
      action: 'all',
      access: 'conditional',
      condition: 'institution_id = {adminInstitutionId}',
    },
    { role: 'staff', action: 'all', access: 'all' },
  ],
  defaultSort: [['start_date', 'DESC']],
  refOrderClause: 'SELECT ZZ.start_date FROM absence_period ZZ WHERE ZZ.id',
  listRowLink: '/admin/absence-period/display/{key}',
  fields: {
    id: {
      seq: 10,
      type: FieldType.Integer,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'main',
      mandatory: true,
      autoIncrement: true,
      listColumn: true,
    },
    institution_id: {
      seq: 20,
      label: 'Institution',
      type: FieldType.Reference,
      fieldGroup: 'main',
      refEntity: Institution,
      mandatory: true,
      listColumn: true,
    },
    institution_group_id: {
      seq: 30,
      label: 'Institution Group',
      type: FieldType.Reference,
      fieldGroup: 'main',
      refEntity: InstitutionGroup,
      mandatory: true,
      listColumn: true,
    },
    start_date: {
      seq: 40,
      label: 'Start Date',
      type: FieldType.Date,
      fieldGroup: 'main',
      mandatory: true,
      listColumn: true,
    },
    end_date: {
      seq: 50,
      label: 'End Date',
      type: FieldType.Date,
      fieldGroup: 'main',
      mandatory: true,
      listColumn: true,
    },
    amber_threshold: {
      seq: 60,
      label: 'Amber Threshold',
      type: FieldType.Integer,
      fieldGroup: 'main',
      mandatory: true,
      description: 'Number of absences triggering amber warning',
    },
    red_threshold: {
      seq: 70,
      label: 'Red Threshold',
      type: FieldType.Integer,
      fieldGroup: 'main',
      mandatory: true,
      description: 'Number of absences triggering red warning',
    },
    member_count: {
      seq: 80,
      label: 'Member Count',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    tracked_event_count: {
      seq: 90,
      label: 'Tracked Event Count',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    booking_count: {
      seq: 100,
      label: 'Booking Count',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
    },
    absences_count: {
      seq: 110,
      label: 'Absences Count',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    attended_count: {
      seq: 115,
      label: 'Attended Count',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
    },
    not_occurred_event_count: {
      seq: 120,
      label: 'Not Occurred Event Count',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
    },
    members_in_amber_count: {
      seq: 130,
      label: 'Members in Amber',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    members_in_red_count: {
      seq: 140,
      label: 'Members in Red',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    last_computed_at: {
      seq: 150,
      label: 'Last Computed At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'main',
    },
    event_ids: {
      seq: 155,
      label: 'Event Ids',
      description: 'Comma-separated list',
      type: FieldType.Text,
      persistence: PersistenceType.BackEndManaged,
    },
    computation_notes: {
      seq: 160,
      label: 'Computation Notes',
      type: FieldType.Text,
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'main',
      formColumnWidths: { lg: 12 },
    },
  },
};
